import type {
	AwaitingStage,
	SpotTradePairResponse,
	TradeKind,
	TradeType,
} from "@/api/types"
import { formatCurrencySymbol, formatNumber } from "@/lib/format"

export const AWAITING_STAGE_LABELS: Record<AwaitingStage, string> = {
	payment: "Payment",
	invoice: "Invoice",
	delivery: "Delivery to buyer",
	receipt: "Receipt from supplier",
}

export function isAwaitingStage(value: unknown): value is AwaitingStage {
	return typeof value === "string" && value in AWAITING_STAGE_LABELS
}

export function tradeDateToUtc(value: string): Date {
	return new Date(`${value}Z`)
}

export const TRADING_PRODUCTS = ["EUA", "UKA", "FuelEU"] as const

export type TradingProduct = (typeof TRADING_PRODUCTS)[number]

export const PRODUCT_CURRENCY: Record<TradingProduct, string> = {
	EUA: "EUR",
	UKA: "GBP",
	FuelEU: "EUR",
}

const COLORS: Record<string, string> = {
	EUA: "bg-cat-4-subtle text-cat-4-foreground",
	UKA: "bg-cat-1-subtle text-cat-1-foreground",
	FuelEU: "bg-cat-3-subtle text-cat-3-foreground",
}

const DEFAULT_COLOR = "bg-muted text-muted-foreground"

const LABELS: Record<string, string> = {
	EUA: "EUA",
	UKA: "UKA",
	FuelEU: "FuelEU",
}

export function getTradingProductColor(product: string): string {
	return COLORS[product] ?? DEFAULT_COLOR
}

export function getTradingProductLabel(product: string): string {
	return LABELS[product] ?? product
}

export const TRADING_PRODUCT_OPTIONS = TRADING_PRODUCTS.map((value) => ({
	value,
	label: getTradingProductLabel(value),
}))

export const KIND_LABELS: Record<TradeKind, string> = {
	trade: "Trade",
	broker: "Broker",
	long: "Long",
	short: "Short",
}

export function isTradeKind(value: unknown): value is TradeKind {
	return typeof value === "string" && value in KIND_LABELS
}

export function isOpenPosition(trade: Pick<SpotTradePairResponse, "kind">) {
	return trade.kind === "long" || trade.kind === "short"
}

export function hasLinkedTrades(
	trade: Pick<SpotTradePairResponse, "kind" | "openQuantity" | "quantity">,
) {
	return isOpenPosition(trade) && trade.openQuantity < trade.quantity
}

export function getFeeLabel(
	tradeType: string,
	product: string,
	side: "buy" | "sell",
): string {
	if (tradeType === "broker") return side === "buy" ? "Seller Fee" : "Buyer Fee"
	if (product === "FuelEU" && side === "sell") return "Pooling Fee"
	return "Flat Fee"
}

export function getFeeDisplay(
	fee: number | null,
	quantity: number,
	currency: string,
	tradeType: string,
): string {
	const symbol = formatCurrencySymbol(currency)
	if (fee == null) return `${symbol}0.00`
	if (tradeType === "broker") {
		return `${symbol}${formatNumber(fee / quantity)}/unit`
	}
	return `${symbol}${formatNumber(fee)}`
}

export interface Stage {
	key: string
	label: string
	description: string
	tradeIdField: "sellTradeId" | "buyTradeId"
	valueField: string
	settledField: "paymentSettled" | "transferSettled" | "invoiced"
}

const DEFAULT_STAGES: Stage[] = [
	{
		key: "collected",
		label: "Payment received",
		description: "Collected",
		tradeIdField: "sellTradeId",
		valueField: "paymentReceivedAt",
		settledField: "paymentSettled",
	},
	{
		key: "paid",
		label: "Payment sent",
		description: "Paid",
		tradeIdField: "buyTradeId",
		valueField: "paymentToVendorAt",
		settledField: "paymentSettled",
	},
	{
		key: "received",
		label: "Product received",
		description: "Received",
		tradeIdField: "buyTradeId",
		valueField: "transferReceivedAt",
		settledField: "transferSettled",
	},
	{
		key: "delivered",
		label: "Product sent",
		description: "Delivered",
		tradeIdField: "sellTradeId",
		valueField: "transferDeliveredAt",
		settledField: "transferSettled",
	},
]

const FUEL_EU_BROKER_STAGES: Stage[] = [
	{
		key: "sellerInvoiced",
		label: "Seller invoiced",
		description: "Seller has invoiced us",
		tradeIdField: "buyTradeId",
		valueField: "sellerInvoicedAt",
		settledField: "invoiced",
	},
	{
		key: "buyerInvoiced",
		label: "Buyer invoiced",
		description: "We have invoiced the buyer",
		tradeIdField: "sellTradeId",
		valueField: "buyerInvoicedAt",
		settledField: "invoiced",
	},
	{
		key: "sellerPaid",
		label: "Seller paid",
		description: "We have paid the seller",
		tradeIdField: "buyTradeId",
		valueField: "paymentToVendorAt",
		settledField: "paymentSettled",
	},
	{
		key: "buyerPaid",
		label: "Buyer paid",
		description: "Buyer has paid us",
		tradeIdField: "sellTradeId",
		valueField: "paymentReceivedAt",
		settledField: "paymentSettled",
	},
]

const FUEL_EU_TRADE_STAGES: Stage[] = [
	{
		key: "invoiced",
		label: "Invoiced",
		description: "We have invoiced the buyer",
		tradeIdField: "sellTradeId",
		valueField: "buyerInvoicedAt",
		settledField: "invoiced",
	},
	{
		key: "paymentReceived",
		label: "Payment received",
		description: "Buyer has paid us",
		tradeIdField: "sellTradeId",
		valueField: "paymentReceivedAt",
		settledField: "paymentSettled",
	},
]

export function getStages(product: string, tradeType: TradeType): Stage[] {
	if (product !== "FuelEU") return DEFAULT_STAGES
	return tradeType === "broker" ? FUEL_EU_BROKER_STAGES : FUEL_EU_TRADE_STAGES
}

export interface LegTotals {
	buy: number | null
	sell: number | null
}

// Cash moving on each leg from our side. On broker trades the buy-leg fee is the
// seller fee, which we receive rather than pay, so it nets off the buy total.
export function getLegTotals(
	trade: Pick<
		SpotTradePairResponse,
		| "quantity"
		| "priceIn"
		| "priceOut"
		| "flatFeeIn"
		| "flatFeeOut"
		| "tradeType"
	>,
): LegTotals {
	const feeInSign = trade.tradeType === "broker" ? -1 : 1
	return {
		buy:
			trade.priceIn == null
				? null
				: trade.priceIn * trade.quantity + feeInSign * (trade.flatFeeIn ?? 0),
		sell:
			trade.priceOut == null
				? null
				: trade.priceOut * trade.quantity + (trade.flatFeeOut ?? 0),
	}
}
